import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, hubspotApi, User } from '../services/api';

interface AuthContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    hubspotConnected: boolean;
    hubspotConfigured: boolean;
    login: () => void;
    logout: () => void;
    refreshUser: () => Promise<void>;
    connectHubSpot: () => void;
    disconnectHubSpot: () => Promise<void>;
    checkHubSpotStatus: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hubspotConnected, setHubSpotConnected] = useState(false);
    const [hubspotConfigured, setHubSpotConfigured] = useState(false);

    const refreshUser = async () => {
        try {
            const userData = await authApi.getCurrentUser();
            setUser(userData);
            // Also check HubSpot status when user data loads
            if (userData) {
                await checkHubSpotStatus();
            }
        } catch {
            setUser(null);
        } finally {
            setIsLoading(false);
        }
    };

    const checkHubSpotStatus = async () => {
        try {
            const status = await hubspotApi.getStatus();
            setHubSpotConnected(status.connected);
            setHubSpotConfigured(status.configured);
        } catch (error) {
            console.error('Failed to check HubSpot status:', error);
            setHubSpotConnected(false);
            setHubSpotConfigured(false);
        }
    };

    const connectHubSpot = () => {
        hubspotApi.connect();
    };

    const disconnectHubSpot = async () => {
        try {
            await hubspotApi.disconnect();
            setHubSpotConnected(false);
        } catch (error) {
            console.error('Failed to disconnect HubSpot:', error);
            throw error;
        }
    };

    const handleCallback = async () => {
        const params = new URLSearchParams(window.location.search);
        const token = params.get('token');
        const userid = params.get('userid');
        const hubspotCallback = params.get('hubspot');

        console.log('handleCallback - URL:', window.location.href);
        console.log('handleCallback - token:', token);
        console.log('handleCallback - userid:', userid);
        console.log('handleCallback - hubspot:', hubspotCallback);

        if (token && userid) {
            try {
                console.log('Sending POST to /api/auth/callback');
                // Send token to API for validation
                const response = await fetch('/api/auth/callback', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ token, userid }),
                });

                console.log('Callback response status:', response.status);

                if (response.ok) {
                    // Clear URL params
                    window.history.replaceState({}, '', window.location.pathname);
                    // Refresh user after successful auth
                    await refreshUser();
                    return;
                }
            } catch (error) {
                console.error('Auth callback failed:', error);
            }
        } else if (hubspotCallback === 'connected') {
            // HubSpot OAuth callback - refresh connection status
            await refreshUser();
            // Clear the query param
            window.history.replaceState({}, '', window.location.pathname);
            return;
        }
        // No callback, just check existing session
        console.log('No token found, checking existing session');
        await refreshUser();
    };

    useEffect(() => {
        handleCallback();
    }, []);

    const login = () => {
        window.location.href = authApi.getLoginUrl();
    };

    const logout = () => {
        authApi.logout();
    };

    const value: AuthContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        hubspotConnected,
        hubspotConfigured,
        login,
        logout,
        refreshUser,
        connectHubSpot,
        disconnectHubSpot,
        checkHubSpotStatus,
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
